// API client for backend communication

import { Effect } from "effect"
import type {
  Workplace,
  Worker,
  MonthScheduleResponse,
  CreateWorkplaceInput,
  UpdateWorkplaceInput,
  CreateWorkerInput,
  UpdateWorkerInput,
  SaveMonthInput,
  WorkplaceId,
  WorkerId,
  MonthString,
} from "@eckersdorf/api"

const API_URL = import.meta.env["VITE_API_URL"] 
  ?? (import.meta.env["DEV"] === "true" ? "http://localhost:8787" : "https://eckersdorf-backend.r-krzywaznia-2c4.workers.dev")

// Auth state
let authToken: string | null = null

export function setAuthToken(token: string | null): void {
  authToken = token
  if (token !== null) {
    localStorage.setItem("auth_token", token)
  } else {
    localStorage.removeItem("auth_token")
  }
}

export function getAuthToken(): string | null {
  if (authToken === null) {
    authToken = localStorage.getItem("auth_token")
  }
  return authToken
}

export function clearAuth(): void {
  authToken = null
  localStorage.removeItem("auth_token")
  localStorage.removeItem("user")
}

// Fetch wrapper with auth
async function fetchApi<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  }

  const token = getAuthToken()
  if (token !== null) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    if (response.status === 401) {
      clearAuth()
      window.location.href = "/"
      // Return a never-resolving promise to halt execution while redirect happens
      return new Promise(() => {})
    }
    const error = await response.json().catch(() => ({ error: "Unknown error" })) as { error?: string }
    throw new Error(error.error ?? `HTTP ${response.status}`)
  }

  return response.json() as Promise<T>
}

// Auth API
export interface LoginResponse {
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    role: string
  }
  token: string
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetchApi<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  })
  setAuthToken(response.token)
  localStorage.setItem("user", JSON.stringify(response.user))
  return response
}

export function logout(): void {
  clearAuth()
}

export function getCurrentUser(): LoginResponse["user"] | null {
  const user = localStorage.getItem("user")
  if (user === null) {
    return null
  }
  try {
    return JSON.parse(user) as LoginResponse["user"]
  } catch {
    return null
  }
}

// Workplaces API
export async function getWorkplaces(): Promise<readonly Workplace[]> {
  return fetchApi<readonly Workplace[]>("/api/workplaces")
}

export async function getWorkplace(id: WorkplaceId): Promise<Workplace> {
  return fetchApi<Workplace>(`/api/workplaces/${id}`)
}

export async function createWorkplace(input: CreateWorkplaceInput): Promise<Workplace> {
  return fetchApi<Workplace>("/api/workplaces", {
    method: "POST",
    body: JSON.stringify(input),
  })
}

export async function updateWorkplace(id: WorkplaceId, input: UpdateWorkplaceInput): Promise<Workplace> {
  return fetchApi<Workplace>(`/api/workplaces/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  })
}

export async function deleteWorkplace(id: WorkplaceId): Promise<void> {
  await fetchApi<{ success: boolean }>(`/api/workplaces/${id}`, {
    method: "DELETE",
  })
}

// Workers API
export async function getWorkers(workplaceId: WorkplaceId): Promise<readonly Worker[]> {
  return fetchApi<readonly Worker[]>(`/api/workplaces/${workplaceId}/workers`)
}

export async function createWorker(input: CreateWorkerInput): Promise<Worker> {
  return fetchApi<Worker>("/api/workers", {
    method: "POST",
    body: JSON.stringify(input),
  })
}

export async function updateWorker(id: WorkerId, input: UpdateWorkerInput): Promise<Worker> {
  return fetchApi<Worker>(`/api/workers/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  })
}

export async function deleteWorker(id: WorkerId): Promise<void> {
  await fetchApi<{ success: boolean }>(`/api/workers/${id}`, {
    method: "DELETE",
  })
}

// Schedules API
export async function getSchedules(
  workplaceId: WorkplaceId,
  month: MonthString
): Promise<MonthScheduleResponse> {
  return fetchApi<MonthScheduleResponse>(`/api/workplaces/${workplaceId}/schedules/${month}`)
}

export async function saveSchedules(input: SaveMonthInput): Promise<void> {
  await fetchApi<{ success: boolean }>(
    `/api/workplaces/${input.workplaceId}/schedules/${input.month}`,
    {
      method: "PUT",
      body: JSON.stringify(input),
    }
  )
}

export async function deleteSchedules(
  workplaceId: WorkplaceId,
  month: MonthString
): Promise<void> {
  await fetchApi<{ success: boolean }>(`/api/workplaces/${workplaceId}/schedules/${month}`, {
    method: "DELETE",
  })
}

export async function getScheduleStats(
  workplaceId: WorkplaceId,
  month: MonthString
): Promise<{
  workplaceId: WorkplaceId
  month: MonthString
  stats: readonly {
    workerId: WorkerId
    hoursTotal: number
    daysWorked: number
    saturdaysWorked: number
    sundaysWorked: number
  }[]
}> {
  return fetchApi(`/api/workplaces/${workplaceId}/schedules/${month}/stats`)
}
